import Sidebar from './components/Sidebar';
import { useLocation, Navigate, Outlet } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { useLocalStorage } from '@uidotdev/usehooks';
import { OrganizationPaymentMethodEnum, UserCurrentActivePlan } from '@EHSNavigator/backend';
import { useEffect } from 'react';
import { useApiClients } from './client/api/useApiClient';
import { useQuery } from '@tanstack/react-query';
import { UserWithOrgContext } from './types/user';
import Loader from './components/Loader';
import PlanExpired from './components/PlanExpired';
import OrgnizationPlanExpire from './components/OrgnizationPlanExpire';
import { useNavigate } from 'react-router';

const PrivateRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useSelector(
    (node: { userLogin: RootState['userLogin'] }) => node?.userLogin,
  );
  const [userInfo, setUserInfo] = useLocalStorage<UserWithOrgContext | null>(
    'userInfo',
    null,
  );
  const { StripeApi, OnboardOrganizationApi } = useApiClients();

  // Only user management routes allowed when orgContext exists
  const allowedRoutesWithOrgContext = [
    '/user-roles',
    '/employee-onboarding',
    '/departments',
    '/bulk-user-creation',
    '/task-review',
  ];

  const isRouteAllowed = (pathname: string) => {
    return allowedRoutesWithOrgContext.some((route) => {
      if (pathname === route) return true;
      if (pathname.startsWith(route + '/')) return true;
      return false;
    });
  };

  const { data, isLoading, refetch } = useQuery<UserCurrentActivePlan>({
    queryKey: ['current-active-plan'],
    queryFn: async () => {
      if (userInfo?.user.org.paymentMethod === OrganizationPaymentMethodEnum.Manual) {
        const response = await OnboardOrganizationApi.onboardOrganizationControllerRequireModal();
        return response?.data;
      }

      const response = await StripeApi.stripeControllerUserCurrentActivePlan();
      return response?.data;
    },
    enabled: isAuthenticated,
  });


  useEffect(() => {
    refetch();
  }, [location.pathname]);

  useEffect(() => {
    if (location?.pathname.replace(/\/$/, '') === '/billing/success')
      setUserInfo((prev: any) => ({
        ...prev,
        currentSubscription: {
          ...prev?.currentSubscription,
          requiresPaymentModal: false,
        },
      }));
  }, [location]);

  useEffect(() => {
    if (userInfo?.orgContext && location.pathname.startsWith('/admin')) {
      navigate('/user-roles');
    }
  }, [userInfo?.orgContext, location.pathname, navigate]);

  useEffect(() => {
    if (userInfo?.orgContext && !isRouteAllowed(location.pathname)) {
      navigate('/user-roles');
    }
  }, [userInfo?.orgContext, location.pathname, navigate]);


  if (isLoading || loading) {
    return <Loader />;
  }

  return isAuthenticated ? (
    location?.pathname.replace(/\/$/, '') === '/billing/cancel' ? (
      <Outlet />
    ) : data?.requiresPaymentModal ? (
      <>
        {userInfo?.user?.role?.name === 'Owner' ? (
          <PlanExpired />
        ) : (
          <OrgnizationPlanExpire />
        )}
      </>
    ) : (
      <>
        {!location?.pathname?.includes('delete-account') && <Sidebar />}
        {!location?.pathname?.includes('delete-account') ? (
          <>
            {userInfo?.orgContext && (
              <div className="lg:ml-310 lg:w-[calc(100%-310px)] bg-neutral-tones-sidebar/80 backdrop-blur-md flex w-full items-center justify-between gap-4 border-b px-15 py-4 md:px-25 lg:px-14 sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <span className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area text-sm leading-5 font-medium lg:text-base lg:leading-6">
                    Viewing {' '}
                    <span className="text-brand-byzantine font-semibold capitalize">
                      {userInfo.orgContext.org.name}
                    </span>
                  </span>
                </div>
                <button
                  onClick={() => {
                    navigate('/admin/customers');
                    setUserInfo((prev) => {
                      if (!prev) return prev;
                      const { orgContext, ...rest } = prev;
                      return { ...rest, orgContext: null };
                    });
                  }}
                  className="cursor-pointer bg-translucent-background/80 border-neutral-tones-text/30 hover:bg-translucent-background hover:border-neutral-tones-text/50 active:bg-translucent-background/90 flex items-center gap-2 rounded-5 border px-4 py-2 transition-all duration-200"
                  aria-label="Exit organization context"
                >
                  <span className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-medium lg:text-base lg:leading-5 group-hover:text-neutral-tones-text/90 transition-colors">
                    Exit
                  </span>
                </button>
              </div>
            )}
            <div className="flex min-h-screen w-full overflow-hidden">
              <main className="flex w-full flex-1 flex-col items-start lg:ml-310 lg:w-[calc(100%-310px)]">
                <Outlet />
              </main>
            </div>
          </>
        ) : (
          <Outlet />
        )}
      </>
    )
  ) : (
    <Navigate
      to={
        `/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`
      }
      replace
    />
  );
};
export default PrivateRoute;
